import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Plus, Trash2, Package, Search, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// Tax rate options
const TAX_RATES = [
  { value: '0', label: '0%' },
  { value: '5', label: '5%' },
  { value: '10', label: '10%' },
  { value: '15', label: '15%' },
  { value: '18', label: '18%' },
  { value: '20', label: '20%' },
];

// Line Item Row Component
const LineItemRow = ({ item, index, products, onUpdate, onRemove }) => {
  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
        if (product) {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(product.price) || 0;
      const taxRate = parseFloat(item.tax_rate) || 0;
      const subtotal = qty * price;
      const taxAmount = subtotal * (taxRate / 100);
      onUpdate(index, {
        ...item,
        product_id: productId,
        product_name: product.name,
        product_code: product.sku || product.code || '',
        description: product.description || product.name,
        unit_price: product.price || 0,
        subtotal,
        tax_amount: taxAmount,
        line_total: subtotal + taxAmount,
        tax_rate: item.tax_rate || '0',
      });
    }
  };

  const handleFieldChange = (field, value) => {
    const updated = { ...item, [field]: value };
    
    // Recalculate line total
    const qty = parseFloat(updated.quantity) || 0;
    const price = parseFloat(updated.unit_price) || 0;
    const taxRate = parseFloat(updated.tax_rate) || 0;
    const subtotal = qty * price;
    const taxAmount = subtotal * (taxRate / 100);
    updated.line_total = subtotal + taxAmount;
    updated.tax_amount = taxAmount;
    updated.subtotal = subtotal;
    
    onUpdate(index, updated);
  };

  return (
    <div className="grid grid-cols-12 gap-2 items-start p-3 bg-slate-50 rounded-lg mb-2">
      {/* Product Selection */}
      <div className="col-span-3">
        <Label className="text-xs text-slate-500 mb-1 block">Product</Label>
        <Select value={item.product_id || ''} onValueChange={handleProductChange}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue placeholder="Select product" />
          </SelectTrigger>
          <SelectContent>
            {products.map(product => (
              <SelectItem key={product.id} value={product.id}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] px-1">
                    {product.sku || product.code || 'N/A'}
                  </Badge>
                  <span className="truncate">{product.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div className="col-span-2">
        <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
        <Input
          value={item.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          placeholder="Description"
          className="h-9 text-sm"
        />
      </div>

      {/* Quantity */}
      <div className="col-span-1">
        <Label className="text-xs text-slate-500 mb-1 block">Qty</Label>
        <Input
          type="number"
          min="1"
          value={item.quantity || 1}
          onChange={(e) => handleFieldChange('quantity', e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      {/* Unit Price */}
      <div className="col-span-2">
        <Label className="text-xs text-slate-500 mb-1 block">Unit Price</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={item.unit_price || 0}
          onChange={(e) => handleFieldChange('unit_price', e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      {/* Tax Rate */}
      <div className="col-span-1">
        <Label className="text-xs text-slate-500 mb-1 block">Tax %</Label>
        <Select value={item.tax_rate || '0'} onValueChange={(val) => handleFieldChange('tax_rate', val)}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TAX_RATES.map(rate => (
              <SelectItem key={rate.value} value={rate.value}>{rate.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Line Total */}
      <div className="col-span-2">
        <Label className="text-xs text-slate-500 mb-1 block">Total</Label>
        <div className="h-9 flex items-center px-3 bg-white border rounded-md text-sm font-medium text-slate-900">
          ${(item.line_total || (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0) * (1 + (parseFloat(item.tax_rate) || 0) / 100)).toFixed(2)}
        </div>
      </div>

      {/* Remove Button */}
      <div className="col-span-1 flex items-end justify-center pb-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Quick Add Client Dialog
const QuickAddClientDialog = ({ open, onClose, onClientCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await api.post('/clients', formData);
      toast.success('Client created successfully');
      onClientCreated(response.data);
      onClose();
      setFormData({ name: '', email: '', phone: '', company: '', address: '' });
    } catch (error) {
      toast.error('Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-teal-600" />
            Add New Client
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Client name"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="grid gap-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1-555-0100"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Company</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Company name"
              />
            </div>
            <div className="grid gap-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Line Items Form Component
const LineItemsForm = ({ 
  items, 
  onItemsChange, 
  clients, 
  onClientsChange,
  selectedClientId,
  onClientChange,
  discount,
  onDiscountChange,
  discountType,
  onDiscountTypeChange,
  partyType = 'client', // 'client' or 'supplier'
  partyLabel = 'Client',
}) => {
  const [products, setProducts] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceHistory, setPriceHistory] = useState({});
  const itemNamesKey = (items || []).map((it) => (it.product_name || '').trim()).filter(Boolean).join('|');
  useEffect(() => {
    const names = [...new Set(itemNamesKey.split('|').filter(Boolean))];
    if (names.length === 0) { setPriceHistory({}); return; }
    let cancelled = false;
    Promise.all(names.map(async (name) => {
      try {
        const params = new URLSearchParams({ item: name });
        if (selectedClientId) params.set('client_id', selectedClientId);
        const res = await api.get(`/price-history?${params.toString()}`);
        return [name, res.data];
      } catch (err) { return [name, null]; }
    })).then((entries) => { if (!cancelled) setPriceHistory(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [itemNamesKey, selectedClientId]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const addLineItem = () => {
    const newItem = {
      id: Date.now().toString(),
      product_id: '',
      product_name: '',
      product_code: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: '0',
      tax_amount: 0,
      subtotal: 0,
      line_total: 0,
    };
    onItemsChange([...items, newItem]);
  };

  const updateLineItem = (index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = updatedItem;
    onItemsChange(newItems);
  };

  const removeLineItem = (index) => {
    if (items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    onItemsChange(newItems);
  };

  const handleClientCreated = (newClient) => {
    onClientsChange([...clients, newClient]);
    onClientChange(newClient.id);
  };

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  const totalTax = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const discountAmount = discountType === 'percent' 
    ? (subtotal * (parseFloat(discount) || 0) / 100)
    : (parseFloat(discount) || 0);
  const grandTotal = subtotal + totalTax - discountAmount;

  // Filter clients by search
  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Client/Supplier Selection */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-sm font-medium">{partyLabel} *</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddClient(true)}
            className="h-8 text-xs"
          >
            <UserPlus className="h-3 w-3 mr-1" />
            Add New {partyLabel}
          </Button>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={`Search ${partyLabel.toLowerCase()}s...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 mb-2"
          />
        </div>
        
        <Select value={selectedClientId || ''} onValueChange={onClientChange}>
          <SelectTrigger>
            <SelectValue placeholder={`Select ${partyLabel.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {filteredClients.map(client => (
              <SelectItem key={client.id} value={client.id}>
                <div className="flex flex-col">
                  <span className="font-medium">{client.name}</span>
                  <span className="text-xs text-slate-500">
                    {client.company && `${client.company} • `}{client.email}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Line Items */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-teal-600" />
            <Label className="text-sm font-medium">Line Items</Label>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addLineItem}
            className="h-8"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Item
          </Button>
        </div>

        {/* Column Headers */}
        <div className="grid grid-cols-12 gap-2 px-3 mb-2 text-xs font-medium text-slate-500">
          <div className="col-span-3">Product</div>
          <div className="col-span-2">Description</div>
          <div className="col-span-1">Qty</div>
          <div className="col-span-2">Unit Price</div>
          <div className="col-span-1">Tax %</div>
          <div className="col-span-2">Total</div>
          <div className="col-span-1"></div>
        </div>

        {/* Line Items */}
        <div className="space-y-2">
          {items.map((item, index) => (
            <LineItemRow
              key={item.id || index}
              item={item}
              index={index}
              products={products}
              onUpdate={updateLineItem}
              onRemove={removeLineItem}
            />
          ))}
        </div>

        {items.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <Package className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p>No items added yet</p>
            <Button
              type="button"
              variant="link"
              onClick={addLineItem}
              className="text-teal-600"
            >
              Add your first item
            </Button>
          </div>
        )}
      </div>

        {/* Price History Suggestions - display only; a price is applied only when you click Use */}
        {(items || []).some((it) => {
          const h = priceHistory[(it.product_name || '').trim()];
          return h && (h.last_quoted != null || h.last_sold != null || h.last_quoted_for_client != null || h.last_sold_for_client != null);
        }) && (
          <div className="space-y-2 rounded-lg border border-teal-100 bg-teal-50/60 p-3">
            <p className="text-xs font-semibold text-teal-800">Price history — suggestions only, nothing is applied until you choose Use</p>
            {(items || []).map((item, index) => {
              const name = (item.product_name || '').trim();
              const hist = name ? priceHistory[name] : null;
              if (!hist || (hist.last_quoted == null && hist.last_sold == null && hist.last_quoted_for_client == null && hist.last_sold_for_client == null)) return null;
              const applyPrice = (price) => {
                const qty = Number(item.quantity) || 0;
                const rate = Number(item.tax_rate) || 0;
                const subtotal = qty * price;
                const taxAmount = subtotal * rate / 100;
                updateLineItem(index, { ...item, unit_price: price, subtotal, tax_amount: taxAmount, line_total: subtotal + taxAmount });
              };
              return (
                <div key={item.id || index} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                  <span className="font-medium text-slate-800">{name}:</span>
                  {hist.last_quoted != null && (<span>Last quoted {hist.last_quoted} <button type="button" onClick={() => applyPrice(hist.last_quoted)} className="font-medium text-teal-700 underline">Use</button></span>)}
                  {hist.last_sold != null && (<span>Last sold {hist.last_sold} <button type="button" onClick={() => applyPrice(hist.last_sold)} className="font-medium text-teal-700 underline">Use</button></span>)}
                  {hist.last_quoted_for_client != null && (<span>Last quoted for this client {hist.last_quoted_for_client} <button type="button" onClick={() => applyPrice(hist.last_quoted_for_client)} className="font-medium text-teal-700 underline">Use</button></span>)}
                  {hist.last_sold_for_client != null && (<span>Last sold to this client {hist.last_sold_for_client} <button type="button" onClick={() => applyPrice(hist.last_sold_for_client)} className="font-medium text-teal-700 underline">Use</button></span>)}
                </div>
              );
            })}
          </div>
        )}
      {/* Totals Section */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex justify-end">
          <div className="w-80 space-y-3">
            {/* Subtotal */}
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-medium">${subtotal.toFixed(2)}</span>
            </div>

            {/* Tax */}
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Tax</span>
              <span className="font-medium">${totalTax.toFixed(2)}</span>
            </div>

            {/* Discount */}
            <div className="flex justify-between items-center gap-2">
              <span className="text-slate-600 text-sm">Discount</span>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discount || 0}
                  onChange={(e) => onDiscountChange(e.target.value)}
                  className="w-20 h-8 text-sm"
                />
                <Select value={discountType || 'fixed'} onValueChange={onDiscountTypeChange}>
                  <SelectTrigger className="w-20 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">$</SelectItem>
                    <SelectItem value="percent">%</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm font-medium w-24 text-right">
                  -${discountAmount.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Grand Total */}
            <div className="flex justify-between pt-3 border-t-2 border-teal-500">
              <span className="font-semibold text-slate-900">Grand Total</span>
              <span className="text-xl font-bold text-teal-700">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Add Client Dialog */}
      <QuickAddClientDialog
        open={showAddClient}
        onClose={() => setShowAddClient(false)}
        onClientCreated={handleClientCreated}
      />
    </div>
  );
};

export default LineItemsForm;
export { TAX_RATES, QuickAddClientDialog };
